// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

// The API gates GET on the same roles as the route and nav item
// (RATE_CARD_READ_ROLES in ./rateCardAccess), and PUT on RATE_CARD_WRITE_ROLES.
export const getRateCard = createAsyncThunk(
  'rateCard/getRateCard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/rate-card')
      return response.data
    } catch (error) {
      const status = error.response && error.response.status
      const message = (error.response && error.response.data && error.response.data.message) ||
        (status === 403 ? 'You do not have access to the rate card.' : error.message) ||
        'Could not load the rate card.'
      return rejectWithValue(message)
    }
  }
)

// Story 1.2: send only the changed values (diffRateCard's `changes`). The API
// answers with the fresh card, which replaces `data` — "last changed" included.
export const updateRateCard = createAsyncThunk(
  'rateCard/updateRateCard',
  async (changes, { rejectWithValue }) => {
    try {
      const response = await axios.put('/v1/rate-card', changes)
      return response.data
    } catch (error) {
      const status = error.response && error.response.status
      const message = (error.response && error.response.data && error.response.data.message) ||
        (status === 403 ? 'You do not have permission to edit the rate card.' : error.message) ||
        'Could not save the rate card.'
      return rejectWithValue(message)
    }
  }
)

export const rateCardSlice = createSlice({
  name: 'rateCard',
  initialState: {
    data: null,
    isLoading: false,
    error: null,
    isSaving: false,
    saveError: null
  },
  reducers: {
    clearSaveError: (state) => {
      state.saveError = null
    }
  },
  extraReducers: (builder) => {
    builder
      // The error stays up during a retry (its button is disabled meanwhile)
      // and clears only once a load succeeds.
      .addCase(getRateCard.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getRateCard.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        state.data = action.payload
      })
      .addCase(getRateCard.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || action.error.message || 'Could not load the rate card.'
      })
      .addCase(updateRateCard.pending, (state) => {
        state.isSaving = true
        state.saveError = null
      })
      .addCase(updateRateCard.fulfilled, (state, action) => {
        state.isSaving = false
        state.data = action.payload
        // The saved card is current, so a stale-load banner no longer applies.
        state.error = null
      })
      .addCase(updateRateCard.rejected, (state, action) => {
        state.isSaving = false
        state.saveError = action.payload || action.error.message || 'Could not save the rate card.'
      })
  }
})

export const { clearSaveError } = rateCardSlice.actions

export default rateCardSlice.reducer
